
import { Link } from 'react-router-dom';

export default function LifestyleDiseasePrevention() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700 cursor-pointer">ホーム</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li>
                <Link to="/health-support" className="text-gray-500 hover:text-gray-700 cursor-pointer">健康サポート</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li className="text-gray-900">生活習慣病予防</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-50 to-orange-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/5ef0eda34436fa64417b12ed82f11f66.jpg"
            alt="生活習慣病予防"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            生活習慣病<span className="text-red-600">予防</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            糖尿病、高血圧、脂質異常症などの予防と管理方法を薬剤師がご提案します
          </p>
        </div>
      </section>

      {/* Main Diseases */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">主な生活習慣病</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl shadow-lg p-8 border border-red-100">
              <div className="w-12 h-12 bg-red-100 text-red-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-drop-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">糖尿病</h3>
              <p className="text-gray-600 mb-4">血糖値が慢性的に高い状態が続く疾患</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• 2型糖尿病が最も多い</li>
                <li>• 合併症のリスクが高い</li>
                <li>• 食事・運動療法が重要</li>
                <li>• 定期的な血糖値チェック</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl shadow-lg p-8 border border-blue-100">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">高血圧</h3>
              <p className="text-gray-600 mb-4">血圧が正常値を超えて高い状態</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• サイレントキラーと呼ばれる</li>
                <li>• 心疾患・脳卒中のリスク</li>
                <li>• 塩分制限が効果的</li>
                <li>• 家庭血圧測定が重要</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl shadow-lg p-8 border border-yellow-100">
              <div className="w-12 h-12 bg-yellow-100 text-yellow-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-oil-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">脂質異常症</h3>
              <p className="text-gray-600 mb-4">血中の脂質バランスが崩れた状態</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• LDL（悪玉）コレステロール高値</li>
                <li>• HDL（善玉）コレステロール低値</li>
                <li>• 中性脂肪高値</li>
                <li>• 動脈硬化の原因</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Prevention Strategies */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">予防・改善戦略</h2>
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">食事療法</h3>
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">糖尿病予防の食事</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• 炭水化物の質と量をコントロール</li>
                    <li>• 食物繊維を多く摂取</li>
                    <li>• 規則正しい食事時間</li>
                    <li>• ゆっくりよく噛んで食べる</li>
                  </ul>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">高血圧予防の食事</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• 塩分を1日6g未満に制限</li>
                    <li>• カリウムを多く含む食品摂取</li>
                    <li>• DASH食（野菜・果物中心）</li>
                    <li>• アルコール摂取量の制限</li>
                  </ul>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">脂質異常症予防の食事</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• 飽和脂肪酸を控える</li>
                    <li>• オメガ3脂肪酸を積極摂取</li>
                    <li>• 水溶性食物繊維を増やす</li>
                    <li>• トランス脂肪酸を避ける</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">運動療法</h3>
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">有酸素運動</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• 週150分以上の中強度運動</li>
                    <li>• ウォーキング、ジョギング</li>
                    <li>• 水泳、サイクリング</li>
                    <li>• 階段昇降、ダンス</li>
                  </ul>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">筋力トレーニング</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• 週2回以上の筋トレ</li>
                    <li>• 大筋群を中心とした運動</li>
                    <li>• 自重トレーニングから開始</li>
                    <li>• 段階的な負荷増加</li>
                  </ul>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">日常活動の増加</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• 1日8000歩以上の歩行</li>
                    <li>• エレベーターより階段利用</li>
                    <li>• 家事や庭仕事を積極的に</li>
                    <li>• 座りっぱなしを避ける</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Assessment */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">リスク評価チェック</h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">生活習慣病リスクセルフチェック</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">生活習慣</h4>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-gray-700">運動不足（週2回未満）</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-gray-700">喫煙習慣がある</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-gray-700">過度の飲酒（日本酒2合以上/日）</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-gray-700">睡眠不足（6時間未満）</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-gray-700">ストレスが多い</span>
                    </label>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">身体的要因</h4>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-gray-700">BMI 25以上</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-gray-700">腹囲 男性85cm・女性90cm以上</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-gray-700">血圧 130/85mmHg以上</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-gray-700">家族歴がある</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="mr-2" />
                      <span className="text-gray-700">40歳以上</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>チェック結果：</strong>
                  3個以上該当する場合は生活習慣病のリスクが高い可能性があります。
                  薬剤師にご相談ください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monitoring */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">定期モニタリング</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-green-50 text-green-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-calendar-check-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">定期健診</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 年1回の健康診断</li>
                <li>• 血液検査（血糖・脂質）</li>
                <li>• 血圧測定</li>
                <li>• 体重・腹囲測定</li>
                <li>• 尿検査</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">家庭でのモニタリング</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 家庭血圧測定</li>
                <li>• 体重・体脂肪率測定</li>
                <li>• 血糖自己測定（必要時）</li>
                <li>• 食事記録</li>
                <li>• 運動記録</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">専門家との連携</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• かかりつけ医との相談</li>
                <li>• 薬剤師による服薬指導</li>
                <li>• 管理栄養士による栄養指導</li>
                <li>• 運動指導士によるプログラム</li>
                <li>• 定期的なフォローアップ</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">生活習慣病予防を始めませんか？</h2>
          <p className="text-xl text-red-100 mb-8">
            薬剤師があなたの生活習慣を見直し、効果的な予防策をご提案いたします
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/health-support"
              className="bg-white text-red-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              健康サポートに戻る
            </Link>
            <Link 
              to="/"
              className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              ホームに戻る
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">健康サポート</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-consultation/health-checkup-explanation" className="hover:text-white cursor-pointer">健康診断結果解説</Link></li>
                <li><Link to="/health-consultation/lifestyle-disease-prevention" className="hover:text-white cursor-pointer">生活習慣病予防</Link></li>
                <li><Link to="/health-consultation/medication-therapy-consultation" className="hover:text-white cursor-pointer">薬物療法相談</Link></li>
                <li><Link to="/health-consultation/health-management-plan" className="hover:text-white cursor-pointer">健康管理計画</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
